import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../redux/store';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { fetchSEO, upsertSEO } from '../../../../../redux/seoSlice';

const SEOManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { headTags, bodyTags, footerTags } = useSelector((state: RootState) => state.seo);

  const [headInput, setHeadInput] = useState('');
  const [bodyInput, setBodyInput] = useState('');
  const [footerInput, setFooterInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchSEO());
  }, [dispatch]);

  useEffect(() => {
    setHeadInput(headTags || '');
    setBodyInput(bodyTags || '');
    setFooterInput(footerTags || '');
  }, [headTags, bodyTags, footerTags]);

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    setError(null);

    try {
      await dispatch(upsertSEO({ headTags: headInput, bodyTags: bodyInput, footerTags: footerInput }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const renderEditor = (value: string, onChange: (e: any) => void) => (
    <div style={styles.editorWrapper}>
      <div style={styles.lineNumbers}>{getLineNumbers(value)}</div>
      <textarea value={value} onChange={onChange} style={styles.textarea} />
    </div>
  );

  return (
    <div style={styles.page}>
      {(saving || success || error) && (
        <div style={styles.overlay}>
          {saving && <CircularProgress size={60} style={{ color: '#fff' }} />}
          {success && (
            <div style={styles.successOverlay}>
              <CheckCircleOutlineIcon style={{ fontSize: 64, color: '#4caf50' }} />
              <h3 style={styles.successText}>Saved!</h3>
            </div>
          )}
          {error && (
            <div style={styles.errorOverlay}>
              <ErrorOutlineIcon style={{ fontSize: 48, color: '#f44336', marginRight: 10 }} />
              <span style={styles.errorText}>{error}</span>
              <button style={styles.closeBtn} onClick={() => setError(null)}>✖</button>
            </div>
          )}
        </div>
      )}

      <div style={saving ? styles.blur : undefined}>
        <div style={styles.header}>
          <h2 style={styles.title}>Global Header and Footer</h2>
          <button onClick={handleSave} style={styles.saveButton}>Save Changes</button>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Header</label>
          {renderEditor(headInput, (e) => setHeadInput(e.target.value))}
          <p style={styles.note}>These scripts will be printed in the &lt;head&gt; section.</p>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Body</label>
          {renderEditor(bodyInput, (e) => setBodyInput(e.target.value))}
          <p style={styles.note}>These scripts will be printed just below the opening &lt;body&gt; tag.</p>
        </div>

        <div style={styles.section}>
          <label style={styles.label}>Footer</label>
          {renderEditor(footerInput, (e) => setFooterInput(e.target.value))}
          <p style={styles.note}>These scripts will be printed above the closing &lt;/body&gt; tag.</p>
        </div>
      </div>
    </div>
  );
};

const getLineNumbers = (value: string) => {
  const lines = value.split('\n').length || 1;
  return Array.from({ length: lines }, (_, i) => `${i + 1}`).join('\n');
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
    background: 'linear-gradient(to bottom right, #1e003e, #2e0050, #230038)',
    color: '#fff',
    fontFamily: 'sans-serif',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#9333ea',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: 500,
    cursor: 'pointer',
  },
  section: {
    marginBottom: 40,
  },
  label: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: 8,
    display: 'block',
  },
  editorWrapper: {
    display: 'flex',
    backgroundColor: '#1a1a1a',
    border: '1px solid #444',
    borderRadius: 6,
    overflow: 'hidden',
  },
  lineNumbers: {
    backgroundColor: '#2a2a2a',
    color: '#888',
    padding: '12px 10px',
    textAlign: 'right',
    fontFamily: 'monospace',
    userSelect: 'none',
    minWidth: 30,
    whiteSpace: 'pre-wrap',
  },
  textarea: {
    width: '100%',
    padding: 12,
    border: 'none',
    fontFamily: 'monospace',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    resize: 'vertical',
    outline: 'none',
    minHeight: 150,
  },
  note: {
    fontSize: 13,
    color: '#bbb',
    marginTop: 8,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
    backgroundColor: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(5px)',
    zIndex: 999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  blur: {
    filter: 'blur(4px)',
    pointerEvents: 'none',
  },
  successOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  successText: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#4caf50',
    margin: 0,
  },
  errorOverlay: {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    padding: '12px 24px',
    borderRadius: 10,
  },
  errorText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#f44336',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#f44336',
    fontSize: 24,
    marginLeft: 12,
    cursor: 'pointer',
  },
};

export default SEOManager;
